import {
  ROLLOUT_OUTCOME_DISPLAY,
  type RolloutOutcome,
  type RolloutResult,
} from '@/definitions/featureFlags'

/** The outcomes that mean this run changed the organization. */
const MOVED: readonly RolloutOutcome[] = ['ENABLED', 'DISABLED']

/** The outcomes that mean the organization was already where it was asked to be. */
const ALREADY: readonly RolloutOutcome[] = ['ALREADY_ENABLED', 'ALREADY_DISABLED']

/**
 * The outcomes an operator has to do something about.
 *
 * Derived rather than listed, so an outcome added to `RolloutOutcome` lands in a
 * group instead of dropping out of the counts: `ROLLOUT_OUTCOME_DISPLAY` is a
 * `Record` over the union, so the compiler makes the new entry mandatory there.
 */
const NEEDS_ATTENTION: readonly RolloutOutcome[] = (
  Object.keys(ROLLOUT_OUTCOME_DISPLAY) as RolloutOutcome[]
).filter((outcome) => !MOVED.includes(outcome) && !ALREADY.includes(outcome))

export interface RolloutSummary {
  /** Organizations this run actually changed, in whichever direction it ran. */
  moved: number
  /** Organizations already in the requested state, so correctly skipped. */
  already: number
  /** Organizations the run did not change and the operator has to chase. */
  needsAttention: number
}

/**
 * Count a rollout's results into the three groups an operator reads.
 *
 * The split is by what is left to do rather than by outcome, and it is
 * direction-agnostic: turning a flag off reads the same way as turning it on,
 * because the question after either run is the same — how many landed, how many
 * were already there, how many still need chasing.
 *
 * Kept out of the component so the grouping can be tested without rendering.
 */
export const summarizeRollout = (results: RolloutResult[]): RolloutSummary => ({
  moved: results.filter((result) => MOVED.includes(result.outcome)).length,
  already: results.filter((result) => ALREADY.includes(result.outcome)).length,
  needsAttention: results.filter((result) => NEEDS_ATTENTION.includes(result.outcome)).length,
})
